import React, { useState, useEffect } from 'react';
import { IoClose, IoSaveOutline, IoDocumentText } from 'react-icons/io5';
import { db } from '../../firebaseConfig';
// eslint-disable-next-line no-unused-vars
import { collection, doc, updateDoc, query, where, getDocs, orderBy, Timestamp, writeBatch } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { getAccountMap, ACCOUNTS } from '../../utils/accountingUtils';
import moment from 'moment';
import '../ClasificarGastoModal.css'; // Reusing styles

const EditarGastoModal = ({ gasto, onClose, onSuccess }) => {
    const { userData } = useAuth();
    const [loading, setLoading] = useState(false);
    const [fetchingAccounts, setFetchingAccounts] = useState(true);
    const [accounts, setAccounts] = useState([]);

    // Form State
    const [amount, setAmount] = useState(gasto.monto || '');
    const [provider, setProvider] = useState(gasto.proveedor || '');
    const [description, setDescription] = useState(gasto.descripcion || '');

    // Dates need careful handling. Firestore Timestamp -> Date String
    const formatDate = (ts) => {
        if (!ts) return moment().format('YYYY-MM-DD');
        return moment(ts.toDate()).format('YYYY-MM-DD');
    };

    const [date, setDate] = useState(formatDate(gasto.fecha));
    const [dueDate, setDueDate] = useState(formatDate(gasto.fechaVencimiento));
    const [selectedAccountId, setSelectedAccountId] = useState(gasto.cuentaContableId || '');
    const [selectedAccountName, setSelectedAccountName] = useState(gasto.categoria || '');

    useEffect(() => {
        if (userData?.condominioId) {
            fetchAccounts();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData]);

    const fetchAccounts = async () => {
        try {
            const q = query(collection(db, "cuentas_contables"), where("condominioId", "==", userData.condominioId), orderBy("numero", "asc"));
            const snap = await getDocs(q);
            const fetched = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            // Filter for Expenses (GASTO) or Assets (ACTIVO)
            setAccounts(fetched.filter(a => a.tipo === 'GASTO' || a.tipo === 'ACTIVO'));
        } catch (error) {
            console.error("Error fetching accounts:", error);
        } finally {
            setFetchingAccounts(false);
        }
    };

    const handleSave = async () => {
        if (!amount || !provider || !description || !selectedAccountId) {
            alert("Por favor completa todos los campos obligatorios.");
            return;
        }

        setLoading(true);
        try {
            const numericAmount = parseFloat(amount);
            const expenseDate = Timestamp.fromDate(new Date(date));
            const expenseDueDate = Timestamp.fromDate(new Date(dueDate));

            const batch = writeBatch(db);

            // 1. Update Gasto Document
            const gastoRef = doc(db, 'gastos', gasto.id);
            batch.update(gastoRef, {
                monto: numericAmount,
                fecha: expenseDate,
                fechaVencimiento: expenseDueDate,
                cuentaContableId: selectedAccountId,
                categoria: selectedAccountName,
                descripcion: description,
                proveedor: provider,
                // Do not update registeredBy or creationDate
            });

            // 2. Update Ledger Entry (Asiento)
            // Find the entry linked to this gasto
            const qAsiento = query(
                collection(db, 'contabilidad_asientos'),
                where('referenciaId', '==', gasto.id)
            );
            const snapAsiento = await getDocs(qAsiento);

            if (!snapAsiento.empty) {
                const asientoDoc = snapAsiento.docs[0];
                const accountMap = await getAccountMap(userData.condominioId);
                const apAccountId = accountMap[ACCOUNTS.PROVEEDORES]; // 2010

                if (apAccountId) {
                    // Rebuild details with new values
                    const detalles = [
                        { cuentaId: selectedAccountId, debe: numericAmount, haber: 0 }, // Expense (Debit)
                        { cuentaId: apAccountId, debe: 0, haber: numericAmount } // AP Liability (Credit)
                    ];

                    batch.update(asientoDoc.ref, {
                        fecha: expenseDate,
                        concepto: `Provisión Gasto: ${provider} - ${description}`,
                        detalles: detalles
                    });
                }
            }

            await batch.commit();

            if (onSuccess) onSuccess();
            onClose();

        } catch (error) {
            console.error("Error updating expense:", error);
            alert("Error al actualizar el gasto.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '500px' }}>
                <div className="modal-header">
                    <h3><IoDocumentText /> Editar Factura (Gasto)</h3>
                    <button onClick={onClose} className="close-btn"><IoClose /></button>
                </div>

                <div className="modal-body">
                    <div className="form-group">
                        <label>Proveedor *</label>
                        <input
                            type="text"
                            className="modal-input"
                            value={provider}
                            onChange={e => setProvider(e.target.value)}
                        />
                    </div>

                    <div className="row" style={{ display: 'flex', gap: '10px' }}>
                        <div className="form-group" style={{ flex: 1 }}>
                            <label>Monto *</label>
                            <input
                                type="number"
                                className="modal-input"
                                value={amount}
                                onChange={e => setAmount(e.target.value)}
                            />
                        </div>
                        <div className="form-group" style={{ flex: 1 }}>
                            <label>Fecha Factura *</label>
                            <input
                                type="date"
                                className="modal-input"
                                value={date}
                                onChange={e => setDate(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="row" style={{ display: 'flex', gap: '10px' }}>
                        <div className="form-group" style={{ flex: 1 }}>
                            <label>Categoría (Cuenta) *</label>
                            {fetchingAccounts ? <p>Cargando...</p> : (
                                <select
                                    className="modal-input"
                                    value={selectedAccountId}
                                    onChange={e => {
                                        const acc = accounts.find(a => a.id === e.target.value);
                                        setSelectedAccountId(e.target.value);
                                        setSelectedAccountName(acc ? `${acc.numero} - ${acc.nombre}` : '');
                                    }}
                                >
                                    <option value="">Selecciona...</option>
                                    {accounts.map(acc => (
                                        <option key={acc.id} value={acc.id}>{acc.numero} - {acc.nombre}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                        <div className="form-group" style={{ flex: 1 }}>
                            <label>Vencimiento</label>
                            <input
                                type="date"
                                className="modal-input"
                                value={dueDate}
                                onChange={e => setDueDate(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Descripción *</label>
                        <input
                            type="text"
                            className="modal-input"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                        />
                    </div>
                </div>

                <div className="modal-actions">
                    <button className="secondary-btn" onClick={onClose}>Cancelar</button>
                    <button className="primary-btn" onClick={handleSave} disabled={loading}>
                        {loading ? 'Guardando...' : <><IoSaveOutline /> Guardar Cambios</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditarGastoModal;
